import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Users, User, Sparkles, Code, Music, Bell, Eye, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({ matches: 0, messages: 0, views: 0 });
  const [onlineFriends, setOnlineFriends] = useState([]);

  useEffect(() => {
    fetchOnlineFriends();
  }, []);

  const fetchOnlineFriends = async () => {
    try {
      const response = await fetch(`${API}/online-friends`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setOnlineFriends(data);
      }
    } catch (error) {
      toast.error("Failed to load dashboard data");
    }
  };

  return (
    <div className="min-h-screen relative z-10">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        {/* Parental Consent Banner */}
        {user?.account_status === "pending_parental_consent" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 border-2 border-[#FF6600] bg-[rgba(255,102,0,0.15)]"
            data-testid="parental-consent-banner"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#FF6600] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[#FF6600] font-bold text-sm">Account Pending Parental Consent</p>
                <p className="text-[#99CCFF] text-xs mt-1">
                  Some features are restricted until a parent/guardian verifies your account.
                </p>
              </div>
              <button
                onClick={() => navigate("/parental-verification")}
                className="myspace-btn text-xs px-3 py-1 flex-shrink-0"
                data-testid="verify-account-btn"
              >
                Verify
              </button>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Summary */}
          <div className="space-y-4">
            {/* Profile Card */}
            <div className="myspace-card">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto border-2 border-[#FF00FF] mb-2 overflow-hidden bg-[#000033]">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl">😎</div>
                  )}
                </div>
                <h2 className="text-[#FF66FF] font-bold text-lg" data-testid="dashboard-welcome">
                  {user?.name || "New User"}
                </h2>
                <p className="text-[#99CCFF] text-sm">"Online and ready to chat!"</p>
                
                {/* Mood */}
                <div className="mood-badge mx-auto mt-2">
                  <span>😊</span>
                  <span>Feeling good</span>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="p-2 bg-[rgba(0,0,0,0.3)] border border-[#6600CC]">
                  <p className="text-[#00FFFF] font-bold text-xl">{stats.matches}</p>
                  <p className="text-[#99CCFF] text-xs">Friends</p>
                </div>
                <div className="p-2 bg-[rgba(0,0,0,0.3)] border border-[#6600CC]">
                  <p className="text-[#FF00FF] font-bold text-xl">{stats.messages}</p>
                  <p className="text-[#99CCFF] text-xs">Messages</p>
                </div>
                <div className="p-2 bg-[rgba(0,0,0,0.3)] border border-[#6600CC]">
                  <div className="view-counter text-sm mx-auto w-fit">
                    {stats.views}
                  </div>
                  <p className="text-[#99CCFF] text-xs mt-1">Views</p>
                </div>
              </div>
              
              <button
                onClick={() => navigate(`/profile/${user?.user_id}`)}
                className="myspace-btn w-full mt-4"
              >
                View My Profile
              </button>
            </div>

            {/* Online Friends */}
            {onlineFriends.length > 0 && (
              <div className="top-friends-box">
                <h3>★ FRIENDS ONLINE ★</h3>
                <div className="space-y-2">
                  {onlineFriends.slice(0, 5).map((friend) => (
                    <div
                      key={friend.user_id}
                      onClick={() => navigate(`/profile/${friend.user_id}`)}
                      className="flex items-center gap-2 cursor-pointer hover:bg-[rgba(255,0,255,0.1)] p-1"
                    >
                      <div className="online-dot"></div>
                      <span className="text-[#FF66FF] text-sm">{friend.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Center Column - Main Actions */}
          <div className="lg:col-span-2 space-y-4">
            {/* Welcome Banner */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="myspace-card text-center"
              style={{ borderColor: '#FFFF00' }}
            >
              <h1 className="text-2xl font-bold mb-2">
                <span className="glitter-text">Welcome back, {user?.name?.split(" ")[0]}!</span>
              </h1>
              <p className="text-[#99CCFF]">What would you like to do today?</p>
            </motion.div>

            {/* Profile Completion */}
            {(!user?.bio || !user?.interests?.length) && (
              <div className="myspace-card" style={{ borderColor: '#FF6600' }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="new-indicator">NEW!</span>
                  <h3 className="text-[#FF6600] font-bold">Complete Your Profile</h3>
                </div>
                <p className="text-[#99CCFF] text-sm mb-3">
                  Add a bio and interests to get more friends!
                </p>
                <button
                  onClick={() => navigate("/edit-profile")}
                  className="myspace-btn-primary"
                  data-testid="complete-profile-btn"
                >
                  Edit Profile Now!
                </button>
              </div>
            )}

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { icon: Heart, title: "Browse", desc: "Find new friends", path: "/discover", color: "#FF00FF" },
                { icon: MessageCircle, title: "Messages", desc: "Check inbox", path: "/matches", color: "#00FFFF" },
                { icon: Users, title: "Bulletins", desc: "What's new", path: "/feed", color: "#FFFF00" },
                { icon: User, title: "My Profile", desc: "View & edit", path: `/profile/${user?.user_id}`, color: "#00FF00" },
                { icon: Code, title: "Customize", desc: "Edit HTML/CSS", path: "/customize", color: "#FF6600" },
                { icon: Music, title: "Music", desc: "Profile song", path: "/customize", color: "#FF00FF" },
              ].map((action, index) => (
                <motion.button
                  key={action.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05, boxShadow: `0 0 20px ${action.color}` }}
                  onClick={() => navigate(action.path)}
                  className="myspace-card text-left"
                  style={{ borderColor: action.color }}
                  data-testid={`action-${action.title.toLowerCase()}`}
                >
                  <action.icon 
                    className="w-8 h-8 mb-2" 
                    style={{ color: action.color, filter: `drop-shadow(0 0 5px ${action.color})` }}
                  />
                  <h3 className="font-bold text-sm" style={{ color: action.color }}>{action.title}</h3>
                  <p className="text-xs text-[#99CCFF]">{action.desc}</p>
                </motion.button>
              ))}
            </div>

            {/* Bulletin Preview */}
            <div className="bulletin-board">
              <h3>★ LATEST BULLETINS ★</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#99CCFF]">
                  <span className="text-[#FF66FF]">System:</span>
                  <span>Welcome to OurSpacez! Customize your profile now!</span>
                </div>
                <div className="flex items-center gap-2 text-[#99CCFF]">
                  <span className="text-[#00FFFF]">★ Tip:</span>
                  <span>Add a profile song to stand out!</span>
                </div>
              </div>
              <button
                onClick={() => navigate("/feed")}
                className="text-[#FF66FF] text-sm mt-2 hover:text-[#00FFFF]"
              >
                View All Bulletins →
              </button>
            </div>

            {/* R.C's Message */}
            <motion.div 
              className="myspace-card"
              style={{ borderColor: '#00FFFF' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 border-2 border-[#00FFFF] flex items-center justify-center text-2xl bg-[#000033]">
                  😎
                </div>
                <div>
                  <p className="text-[#00FFFF] font-bold">★ R.C ★</p>
                  <p className="text-[#99CCFF] text-sm">
                    "Hey! Thanks for joining OurSpacez! I'm your first friend. 
                    Make sure to customize your profile and add some friends!"
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}
